<template>
	<div v-if="recoms?.success" class="flex flex-wrap relative flex-1">
		<div class="text-center w-full mb-8">
			<span :class="titleStyles || 'text-3xl'">{{ blok.title }}</span>
		</div>
		<template v-if="blok.is_carousel">
			<Carousel
				:slides="products"
				:blok="blok"
				:type-of-slide="ItemType.PRODUCT"
				:show-add-to-cart="blok.show_add_to_cart"
				@product-click="trackProductClick"
			/>
		</template>
		<div v-else class="grid grid-cols-2 lg:grid-cols-4 gap-8 w-full">
			<ProductCard
				v-for="product in products"
				:key="product.productNumber"
				:product="product"
				:show-add-to-cart="blok.show_add_to_cart"
				picture-size="w-full h-auto"
				picture-aspect-ratio="4/5"
				class="flex flex-col"
				@product-click="trackProductClick(product)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { BlokProductRecommendations } from "~/types/storyblok/storyblok";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { ItemType } from "~/utils/constants";

const { helloRetailApi } = useNorcePwa();
const requestURL = useRequestURL();

const { selectedProduct } = useProductStore();
const {
	public: {
		norce: { isHelloRetailTracking }
	}
} = useRuntimeConfig();

const { hierarchies, blok } = defineProps<{
	blok: BlokProductRecommendations;
	titleStyles?: string;
	hierarchies?: string[][];
}>();

interface Context {
	productNumbers?: string[];
	urls?: string[];
	hierarchies?: string[][];
}

const context: Context = {};

const fullPath = computed(() => requestURL.href);

if (selectedProduct?.PartNo) {
	context.productNumbers = [selectedProduct.PartNo];
} else if (hierarchies) {
	context.urls = [fullPath.value];
	context.hierarchies = hierarchies;
}

const productStore = useProductStore();

const { data: recoms } = await useAsyncData("recoms", () =>
	$fetch("/api/recoms", {
		method: "POST",
		body: {
			context,
			productBoxKey: blok.product_box_key
		},
		headers: {
			"Content-Type": "application/json"
		}
	})
);

const toProductItem = (product) =>
	({
		trackingCode: product.trackingCode,
		inStock: product.inStock,
		UniqueName: product.extraData?.uniqueName,
		Files: [{ Path: product.imgUrl }],
		Name: product.title,
		PriceIncVat: product.price,
		OldPrice: product.oldPrice,
		PartNo: product.productNumber,
		PriceListId: productStore?.priceLists?.[0]?.Id
	}) as ProductItem;

const products = computed(() =>
	recoms.value?.success ? recoms.value.products.map(toProductItem) : []
);

const trackProductClick = async (product: object) => {
	if (!isHelloRetailTracking) return;

	helloRetailApi.track({
		serviceType: "click",
		trackingData: {
			source: product.trackingCode
		}
	});
};
</script>
